import { getBranding } from "client/context/branding-context";
import { CountryList } from "client/lib/apt-merchant";
import { getIntl } from "client/utils/internationalization";

import { Field } from "../../field";
import { BusinessOwner } from "../../fieldsets";
import { Merchant } from "../../merchant";
import { I18nValues, MerchantField } from "../../types";
import { fileUploadS3TestValue } from "../../utils/constants";

import { determineGovIdBacksideApplicable } from "./GovIdBacksideField";

type Params = {
  registeredCountry?: CountryList;
};

const approvedCountries: CountryList[] = [
  "Canada",
  "United Kingdom",
  "Australia",
  "New Zealand",
  "United States",
];

export const determineGovIdApplicable = ({
  registeredCountry,
}: Params): boolean => {
  if (
    registeredCountry !== undefined &&
    approvedCountries.includes(registeredCountry)
  ) {
    return true;
  }

  return false;
};

export class GovIdField extends Field<string> {
  protected readonly _owner: BusinessOwner;

  constructor(
    model: Merchant,
    owner: BusinessOwner,
    opts: Partial<MerchantField<string>> = {}
  ) {
    super(model, {
      dataType: "string",
      testValue: fileUploadS3TestValue,
      propertyName: "govId",
      uiFieldType: "FileUpload",
      ...opts,
    });

    this._owner = owner;
  }

  get applicable(): boolean {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    return determineGovIdApplicable({
      registeredCountry,
    });
  }

  get required(): boolean {
    const { requireId } = this._owner.controlValues;
    return this.applicable && requireId;
  }

  get shown(): boolean {
    return this.applicable;
  }

  get uploadFilename(): string {
    const firstName = this._owner.firstName.value;
    const lastName = this._owner.lastName.value;
    return `${firstName}_${lastName}_govId`;
  }

  get i18nValues(): I18nValues {
    const country = this._model.companyInformation.registeredCountry.value;
    const brandTitle = getBranding();
    const t = getIntl(country);
    const labelKey = determineGovIdBacksideApplicable(country)
      ? "govIdFront"
      : this.propertyName;
    return {
      fieldHeader: t(`${this.propertyName}_FieldHeader`, { brandTitle }),
      helperText: t(`${this.propertyName}_HelperText`, { brandTitle }),
      label: t(labelKey, { brandTitle }),
      placeholder: t(`${this.propertyName}_Placeholder`, { brandTitle }),
    };
  }
}
